import { Directive, ElementRef, Input, OnInit, inject, input, output } from '@angular/core';

import { NgAceSidebarStateService } from '../services/sidebar-state.service';

import { NgAceSidebar } from './ace-sidebar.directive';

import Util from '@ace/util';
import { SidebarItem } from '@app/_controls/sidebar/sidebar-item';

@Directive({
  selector: '[NgAceSubmenu]',
  exportAs: 'NgAceSubmenu',
})
export class NgAceSubmenu implements OnInit {
  private readonly sidebarStateService = inject(NgAceSidebarStateService);
  private readonly ngAceSidebar = inject(NgAceSidebar);
  private readonly el = inject(ElementRef);

  readonly items = input.required<SidebarItem[]>();
  currentOpenItem!: SidebarItem | null;

  @Input() autoOpen!: boolean; // open the 'active' elements (submenus) by default
  readonly closeOthers = input(true); // when another one is opened, close one submenu
  readonly itemToggled = output<SidebarItem>();

  element: HTMLElement;
  sidebarId: string | null = null;

  constructor() {
    this.currentOpenItem = null;
    this.element = this.el.nativeElement;
  }

  ngOnInit(): void {
    this.sidebarId = this.ngAceSidebar.element.id || null;

    this.autoOpen = this.autoOpen || this.ngAceSidebar.autoOpen;

    if (!this.autoOpen || !this.sidebarId) return;

    this.items()?.forEach((item: SidebarItem) => {
      if (this.sidebarId && item.id && this.sidebarStateService.isOpen(this.sidebarId, item.id)) {
        item.open = true;
        this.itemToggled.emit(item);
        this.currentOpenItem = item; // if we come back from another layout (like login) to main, and a submenu is open, we assign it to currentOpenItem, so it will be closed once another submenu is opened
      }
    });
  }

  // also close the previous "open" submenu if "closeOthers" is true
  toggle(navItem: SidebarItem, navItemSub?: NgAceSubmenu) {
    navItem.open = !navItem.open;
    this.itemToggled.emit(navItem);

    if (!this.autoOpen || !this.sidebarId) return;

    if (navItem.open && this.currentOpenItem != navItem) {
      this.closePreviousItem();

      if (this.sidebarId && navItem.id) {
        this.sidebarStateService.setOpen(this.sidebarId, navItem.id);
      }
      this.currentOpenItem = navItem;
    }

    if (navItem.open && navItemSub) {
      // navItemSub is the current submenu related to navItem, being opened
      // and we scroll it into view when opened
      const submenuEl = Util.closest(navItemSub.element, '.submenu');
      if (!submenuEl) return;
      this.ngAceSidebar.sidebar?._submenuScroll(submenuEl, 180); // wait 180ms before scrolling
    }
  }

  private closePreviousItem(): void {
    if (!this.currentOpenItem?.open || !this.closeOthers()) return;

    if (this.sidebarId && this.currentOpenItem.id) {
      this.sidebarStateService.unOpen(this.sidebarId, this.currentOpenItem.id);
    }
    this.currentOpenItem.isToggling = true;
    this.currentOpenItem.open = false;
    this.itemToggled.emit(this.currentOpenItem);
  }
}
